/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { isEqual, isNil } from 'lodash';
import { GOOGLE_MAP_INSTANCE_KEY } from '../../components/common/map/GoogleMapSingleton';
import { MapSizesEnum } from '../../components/common/map_settings/MapSizeController';
import { ToggleTileStateEnum } from '../../components/materialui_wrappers/ToggleTile';
import { Action, ActionCreators, TRANSPORT_MONITORING_MAP_ID } from './actions';
import { Action as GlobalAction, ActionCreators as GlobalActionCreators } from '../actions';
import { Feature } from 'geojson';
import GroundStation from '../../shared/entities/groundStation';
import { LegInfo } from '../../components/common/map_overlays/RulerToolDistanceSummary';
import { AL_MAP_VERSION_PREFIX } from '../../shared/entities/beam';

export enum OverlaysContextsEnum {
  FLEET_VIEW = 'FLEET_VIEW',
  FLEET_VIEW_SELECTED_TAIL = 'FLEET_VIEW_SELECTED_TAIL',
  FTV = 'FTV',
  FTV_EXPORT = 'FTV_EXPORT',
  PERFORMANCE_OUTLOOK = 'PERFORMANCE_OUTLOOK',
  PERFORMANCE_OUTLOOK_EXPORT = 'PERFORMANCE_OUTLOOK_EXPORT',
  TRANSPORT_MONITORING = 'TRANSPORT_MONITORING',
  FULL_SPACE_VEHICLE_VIEW = 'FULL_SPACE_VEHICLE_VIEW',
}

export type OverlaysContextsType = keyof typeof OverlaysContextsEnum;

export enum RlFlToggleStateEnum {
  ReturnLink,
  ForwardLink,
}

export enum TailTagSize {
  SMALL = 'SMALL',
  LARGE = 'LARGE',
}

export interface BeamOnMap {
  beamId: string;
  networkType: string;
  mapType: string;
}

export const LOCAL_STORAGE_TERMINAL_TAGS_KEY = 'up_terminal_tags_state';
export const LOCAL_STORAGE_TERMINAL_TAGS_KEY_V2 = 'up_terminal_tags_state_v2';

/**
 * Load the local storage state from local storage based on the context provided.
 * @param context the context type to grab from local storage
 */
export const loadTerminalTagsLocalStorage = (
  context: OverlaysContextsEnum,
  mapId: string = GOOGLE_MAP_INSTANCE_KEY,
) => {
  let initalTerminalTagsStore = {};

  // migrate any old versions of the local storage over to the new version and delete
  try {
    const locStorageSerializedDataV1 = localStorage.getItem(LOCAL_STORAGE_TERMINAL_TAGS_KEY);

    if (locStorageSerializedDataV1) {
      const locStorageObj = JSON.parse(locStorageSerializedDataV1);
      const stateToSave = {
        [GOOGLE_MAP_INSTANCE_KEY]: { ...locStorageObj },
      };

      localStorage.setItem(LOCAL_STORAGE_TERMINAL_TAGS_KEY_V2, JSON.stringify(stateToSave));
      localStorage.removeItem(LOCAL_STORAGE_TERMINAL_TAGS_KEY);
    }
  } catch (error) {
    console.error('Error migrating between local storage schemas');
  }

  try {
    const locStorageSerializedDataV2 = localStorage.getItem(LOCAL_STORAGE_TERMINAL_TAGS_KEY_V2);

    if (locStorageSerializedDataV2) {
      const locStorageObj = JSON.parse(locStorageSerializedDataV2);

      if (locStorageObj[mapId] && locStorageObj[mapId][context]) {
        initalTerminalTagsStore = {
          terminalTagSize: locStorageObj[mapId][context].terminalTagSize,
          tailTagsShown: locStorageObj[mapId][context].tailTagsShown,
        };
      }
    }
  } catch (error) {
    // ignore error and return empty object
  }

  return initalTerminalTagsStore;
};

export interface OverlaysState {
  antennaDropDownDisabled: boolean;
  selectedAntennaTypeId: number | undefined;
  selectedSscfVersion: string | undefined;
  kuHnaZonesChecked: boolean;
  kaHnaZonesChecked: boolean;
  hcxHnaZonesChecked: boolean;
  gxHnaZonesChecked: boolean;
  kuHnaZonesDisabled: boolean;
  kaHnaZonesDisabled: boolean;
  hcxHnaZonesDisabled: boolean;
  gxHnaZonesDisabled: boolean;
  rlFlToggleState: RlFlToggleStateEnum;
  heatMapsEnabled: boolean;
  groundStationsEnabled: boolean;
  beamsOnMap: BeamOnMap[];
  hubsOnMap: string[];
  groundStationsOnMap: GroundStation[];
  tailTagsShown: boolean;
  terminalTagSize: TailTagSize;
  openedExpansionPanelId: string | null;
  currentBeams?: BeamOnMap[];
  nextBeam?: BeamOnMap;
  weatherShown?: boolean;

  flightPathBeamsDisabled: boolean;
  flightPathBeamsChecked: boolean;

  flightPathWaypointsDisabled: boolean;
  flightPathWaypointsChecked: boolean;

  currentNextBeamsDisabled: boolean;
  currentNextBeamsChecked: boolean;

  gpsSaMapLayersChecked: boolean;
  gpsSaMapLayersLowAccuracyChecked: boolean;
  gpsSaMapLayersReducedAccuracyChecked: boolean;
  gpsSaMapLayersNormalAccuracyChecked: boolean;
}

export interface MapContextType {
  // Map Dimensions
  mapDimensions?: { width: string; height: string };

  // Map Position
  mapPosition?: { top?: number; left?: number }; // Number represents pixels

  // Map Mission Markers
  missionPlanTakeoffMarker?: google.maps.Marker;
  missionPlanLandingMarker?: google.maps.Marker;
  missionPlanPathMarkers?: google.maps.Marker[];

  // Map Settings Panel
  mapSettingsPanelShown: boolean;
  mapSettingsSatViewTileState: ToggleTileStateEnum;
  mapSettingsLabelsTileState: ToggleTileStateEnum;
  mapSettingsPrecipRadarTileState: ToggleTileStateEnum;
  mapSettingsFtvMapSize: MapSizesEnum;
  mapSettingsFtvMapHeight: number;

  // Performance Outlook Legend Panel
  performanceOutlookLegendPanelShown: boolean;

  // Map Overlays Panel
  overlaysPanelShown: boolean;

  // Map Tail Tag Overlays Panel
  tailTagOverlaysPanelShown: boolean;

  // Map Ruler Tool
  mapRulerToolShown: boolean;
  mapRulerToolFollowAircraft: boolean;
  mapRulerToolWaypoints: google.maps.LatLng[];
  mapRulerToolLegData: LegInfo[];
  mapRulerToolPolylinesAndMarkers: {
    markers: google.maps.Marker[];
    polylines: google.maps.Polyline[];
  };

  // Overlays Ui States
  overlaysUiState: { [key in OverlaysContextsType]: OverlaysState };

  // Mission Overlay
  readonly overlaidFlightLegHidden: boolean;

  // Zoom Values
  zoomLatLng?: { lat: number; lng: number };
  savedZoomSettings?: { center: google.maps.LatLng; zoom: number };

  hidden: boolean;
}

export interface State {
  overlaysCurrentContext: OverlaysContextsEnum;
  mapContext: { [mapId: string]: MapContextType };
}

export const defaultOverlaysState: OverlaysState = {
  antennaDropDownDisabled: false,
  selectedAntennaTypeId: undefined,
  selectedSscfVersion: undefined,
  kuHnaZonesChecked: false,
  kaHnaZonesChecked: false,
  hcxHnaZonesChecked: false,
  gxHnaZonesChecked: false,
  kuHnaZonesDisabled: true,
  kaHnaZonesDisabled: true,
  hcxHnaZonesDisabled: true,
  gxHnaZonesDisabled: true,
  rlFlToggleState: RlFlToggleStateEnum.ForwardLink,
  heatMapsEnabled: false,
  groundStationsEnabled: false,
  beamsOnMap: [],
  hubsOnMap: [],
  groundStationsOnMap: [],
  tailTagsShown: true,
  terminalTagSize: TailTagSize.LARGE,
  openedExpansionPanelId: null,
  currentBeams: [],
  nextBeam: undefined,
  weatherShown: undefined,

  flightPathBeamsDisabled: true,
  flightPathBeamsChecked: false,
  flightPathWaypointsDisabled: true,
  flightPathWaypointsChecked: false,

  currentNextBeamsChecked: true,
  currentNextBeamsDisabled: true,

  gpsSaMapLayersChecked: false,
  gpsSaMapLayersLowAccuracyChecked: false,
  gpsSaMapLayersReducedAccuracyChecked: false,
  gpsSaMapLayersNormalAccuracyChecked: false,
};

export const defaultOverlaysStateFtv = {
  ...defaultOverlaysState,
  currentNextBeamsChecked: true,
  currentNextBeamsDisabled: false,
};

export const defaultMapContextState: MapContextType = {
  mapSettingsPanelShown: false,
  mapSettingsSatViewTileState: ToggleTileStateEnum.OFF,
  mapSettingsLabelsTileState: ToggleTileStateEnum.OFF,
  mapSettingsPrecipRadarTileState: ToggleTileStateEnum.OFF,
  mapSettingsFtvMapSize: MapSizesEnum.MEDIUM,
  mapSettingsFtvMapHeight: 0,

  performanceOutlookLegendPanelShown: false,

  overlaysPanelShown: false,

  tailTagOverlaysPanelShown: false,

  mapRulerToolShown: false,

  mapRulerToolFollowAircraft: false,

  mapRulerToolWaypoints: [],

  mapRulerToolLegData: [],

  mapRulerToolPolylinesAndMarkers: {
    markers: [],
    polylines: [],
  },

  overlaidFlightLegHidden: false,

  zoomLatLng: undefined,

  hidden: false,

  overlaysUiState: {
    FLEET_VIEW: {
      ...defaultOverlaysState,
      ...loadTerminalTagsLocalStorage(OverlaysContextsEnum.FLEET_VIEW),
    },
    FLEET_VIEW_SELECTED_TAIL: {
      ...defaultOverlaysState,
      ...loadTerminalTagsLocalStorage(OverlaysContextsEnum.FLEET_VIEW_SELECTED_TAIL),
    },
    FTV: {
      ...defaultOverlaysStateFtv,
      ...loadTerminalTagsLocalStorage(OverlaysContextsEnum.FTV),
    },
    FTV_EXPORT: {
      ...defaultOverlaysStateFtv,
      ...loadTerminalTagsLocalStorage(OverlaysContextsEnum.FTV_EXPORT),
    },
    PERFORMANCE_OUTLOOK: {
      ...defaultOverlaysState,
      ...loadTerminalTagsLocalStorage(OverlaysContextsEnum.PERFORMANCE_OUTLOOK),
    },
    PERFORMANCE_OUTLOOK_EXPORT: {
      ...defaultOverlaysState,
      ...loadTerminalTagsLocalStorage(OverlaysContextsEnum.PERFORMANCE_OUTLOOK_EXPORT),
    },
    TRANSPORT_MONITORING: {
      ...defaultOverlaysState,
      ...loadTerminalTagsLocalStorage(OverlaysContextsEnum.TRANSPORT_MONITORING),
    },
    FULL_SPACE_VEHICLE_VIEW: {
      ...defaultOverlaysState,
      ...loadTerminalTagsLocalStorage(OverlaysContextsEnum.FULL_SPACE_VEHICLE_VIEW),
    },
  },
};

export const initialMapsState: State = {
  overlaysCurrentContext: OverlaysContextsEnum.FLEET_VIEW,
  mapContext: {
    [GOOGLE_MAP_INSTANCE_KEY]: {
      ...defaultMapContextState,
      overlaysUiState: {
        ...defaultMapContextState.overlaysUiState,
      },
    },
    [TRANSPORT_MONITORING_MAP_ID]: {
      ...defaultMapContextState,
      overlaysUiState: {
        ...defaultMapContextState.overlaysUiState,
      },
    },
  },
};

// Redux Reducer
export default function reducer(state: State = initialMapsState, action: Action | GlobalAction): State {
  /**
   * Handle Global Actions
   */
  switch (action.type) {
    case GlobalActionCreators.SwitchOverlaysContext.type: {
      const mapContextObj = Object.entries(state.mapContext).reduce(
        (accum: { [key: string]: MapContextType }, [key, val]) => {
          accum[key] = {
            ...val,
            overlaysUiState: {
              ...val.overlaysUiState,
              [action.payload]: {
                ...val.overlaysUiState[action.payload],
                antennaDropDownDisabled:
                  action.payload !== OverlaysContextsEnum.FLEET_VIEW &&
                  action.payload !== OverlaysContextsEnum.TRANSPORT_MONITORING,
              },
            },
          };

          return accum;
        },
        {},
      );

      return {
        ...state,
        overlaysCurrentContext: action.payload,
        mapContext: mapContextObj,
      };
    }
  }

  /**
   * Map Actions
   */
  let mapId = action.payload?.mapId;

  let currentState = state;
  // map actions require a mapId to act on
  if (!mapId) {
    return currentState;
  } else if (!currentState.mapContext[mapId]) {
    currentState = {
      ...currentState,
      mapContext: {
        ...currentState.mapContext,
        [mapId]: { ...defaultMapContextState },
      },
    };
  }

  let mapFields = currentState.mapContext[mapId];
  switch (action.type) {
    case ActionCreators.SetMapDimensions.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            mapDimensions: { width: action.payload.width, height: action.payload.height },
          },
        },
      };

    case ActionCreators.ClearMapDimensions.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            mapDimensions: undefined,
          },
        },
      };

    case ActionCreators.SetMapPosition.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            mapPosition: { top: action.payload.top, left: action.payload.left },
          },
        },
      };

    case ActionCreators.ClearMapPosition.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            mapPosition: undefined,
          },
        },
      };

    case ActionCreators.SetMissionPlanTakeoffMarker.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            missionPlanTakeoffMarker: action.payload.marker,
          },
        },
      };

    case ActionCreators.SetMissionPlanLandingMarker.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            missionPlanLandingMarker: action.payload.marker,
          },
        },
      };

    case ActionCreators.SetMissionPlanPathMarkers.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            missionPlanPathMarkers: action.payload.markers,
          },
        },
      };

    case ActionCreators.ToggleMapSettingsPanel.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            mapSettingsPanelShown: !mapFields?.mapSettingsPanelShown,
          },
        },
      };

    case ActionCreators.ShowMapSettingsPanel.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            mapSettingsPanelShown: true,
          },
        },
      };

    case ActionCreators.HideMapSettingsPanel.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            mapSettingsPanelShown: false,
          },
        },
      };

    case ActionCreators.SetMapSettingsSatView.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            mapSettingsSatViewTileState: action.payload.tileState,
          },
        },
      };

    case ActionCreators.SetMapSettingsLabels.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            mapSettingsLabelsTileState: action.payload.tileState,
          },
        },
      };

    case ActionCreators.SetMapSettingsPrecipRadar.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            mapSettingsPrecipRadarTileState: action.payload.tileState,
          },
        },
      };

    case ActionCreators.SetMapSettingsFTVMapSize.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            mapSettingsFtvMapSize: action.payload.size,
            mapSettingsFtvMapHeight: action.payload.height,
          },
        },
      };
    case ActionCreators.TogglePerformanceOutlookLegendPanel.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            performanceOutlookLegendPanelShown: !mapFields?.performanceOutlookLegendPanelShown,
          },
        },
      };

    case ActionCreators.ShowPerformanceOutlookLegendPanel.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            performanceOutlookLegendPanelShown: true,
          },
        },
      };

    case ActionCreators.HidePerformanceOutlookLegendPanel.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            performanceOutlookLegendPanelShown: false,
          },
        },
      };

    case ActionCreators.ToggleOverlaysPanel.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysPanelShown: !mapFields?.overlaysPanelShown,
          },
        },
      };

    case ActionCreators.ShowOverlaysPanel.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysPanelShown: true,
          },
        },
      };

    case ActionCreators.HideOverlaysPanel.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysPanelShown: false,
          },
        },
      };

    case ActionCreators.ToggleTailTagOverlaysPanel.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            tailTagOverlaysPanelShown: !mapFields?.tailTagOverlaysPanelShown,
          },
        },
      };

    case ActionCreators.ShowTailTagOverlaysPanel.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            tailTagOverlaysPanelShown: true,
          },
        },
      };

    case ActionCreators.HideTailTagOverlaysPanel.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            tailTagOverlaysPanelShown: false,
          },
        },
      };

    case ActionCreators.ToggleMapRulerTool.type:
      // Set to the opposite of its current value
      const updatedRulerToolShown = !mapFields?.mapRulerToolShown;

      // If the ruler tool is being shown, set the followAircraft flag to the payload value.
      // Set it to false if the ruler is hidden
      const followAircraft = updatedRulerToolShown ? action.payload.followAircraft : false;

      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            mapRulerToolShown: updatedRulerToolShown,
            mapRulerToolFollowAircraft: followAircraft,
            mapRulerToolWaypoints: [],
          },
        },
      };

    case ActionCreators.ShowMapRulerTool.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            mapRulerToolShown: true,
            mapRulerToolFollowAircraft: action.payload.followAircraft,
            mapRulerToolWaypoints: [],
          },
        },
      };

    case ActionCreators.HideMapRulerTool.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            mapRulerToolShown: false,
            mapRulerToolFollowAircraft: false,
            mapRulerToolWaypoints: [],
          },
        },
      };

    case ActionCreators.AppendLatLngToMapRulerTool.type:
      const appendedWaypoints = mapFields?.mapRulerToolWaypoints || [];
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            mapRulerToolWaypoints: [...appendedWaypoints, action.payload.latLng],
          },
        },
      };

    case ActionCreators.PrependLatLngToMapRulerTool.type:
      const prependedWaypoints = mapFields?.mapRulerToolWaypoints || [];
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            mapRulerToolWaypoints: [action.payload.latLng, ...prependedWaypoints],
          },
        },
      };

    case ActionCreators.ClearLatLngsInMapRulerTool.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            mapRulerToolWaypoints: [],
          },
        },
      };

    case ActionCreators.UpdateLatLngInMapRulerTool.type:
      const index = action.payload.index;
      const latLng = action.payload.latLng;
      let waypoints = mapFields?.mapRulerToolWaypoints || [];

      if (waypoints[index]) {
        waypoints = [...waypoints.slice(0, index), latLng, ...waypoints.slice(index + 1)];
      } else if (waypoints.length === 0 && index === 0) {
        waypoints = [latLng];
      }

      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            mapRulerToolWaypoints: waypoints,
          },
        },
      };

    case ActionCreators.RemoveLatLngInMapRulerTool.type:
      const removeIndex = action.payload.index;
      let updatedWaypoints = mapFields?.mapRulerToolWaypoints || [];

      if (removeIndex >= 0 && removeIndex < updatedWaypoints.length) {
        updatedWaypoints = updatedWaypoints.filter((_, index) => index !== removeIndex);
      }

      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            mapRulerToolWaypoints: updatedWaypoints,
          },
        },
      };

    case ActionCreators.SetLegDataMapRulerTool.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            mapRulerToolLegData: action.payload.legData,
          },
        },
      };

    case ActionCreators.ClearLegDataMapRulerTool.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            mapRulerToolLegData: [],
          },
        },
      };

    // The component calling this action is responsible for cleaning up (ie removing from map) any deleted markers or polylines
    case ActionCreators.SetMarkersAndPolylinesMapRulerTool.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            mapRulerToolPolylinesAndMarkers: {
              markers: [...action.payload.markersAndPolylines.markers],
              polylines: [...action.payload.markersAndPolylines.polylines],
            },
          },
        },
      };

    case ActionCreators.SetSelectedAntennaTypeId.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                selectedAntennaTypeId: action.payload.antennaTypeId,
                kuHnaZonesChecked: false,
                kaHnaZonesChecked: false,
                hcxHnaZonesChecked: false,
                gxHnaZonesChecked: false,
              },
            },
          },
        },
      };
    }

    case ActionCreators.SetKuHnaZonesChecked.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                kuHnaZonesChecked: action.payload.checked,
              },
            },
          },
        },
      };
    }

    case ActionCreators.SetKaHnaZonesChecked.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                kaHnaZonesChecked: action.payload.checked,
              },
            },
          },
        },
      };
    }

    case ActionCreators.SetHcxHnaZonesChecked.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                hcxHnaZonesChecked: action.payload.checked,
              },
            },
          },
        },
      };
    }

    case ActionCreators.SetGxHnaZonesChecked.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                gxHnaZonesChecked: action.payload.checked,
              },
            },
          },
        },
      };
    }

    case ActionCreators.SwitchToFlContours.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                rlFlToggleState: RlFlToggleStateEnum.ForwardLink,
              },
            },
          },
        },
      };

    case ActionCreators.SwitchToRlContours.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                rlFlToggleState: RlFlToggleStateEnum.ReturnLink,
              },
            },
          },
        },
      };

    case ActionCreators.EnableHeatMaps.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                heatMapsEnabled: true,
              },
            },
          },
        },
      };

    case ActionCreators.DisableHeatMaps.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                heatMapsEnabled: false,
              },
            },
          },
        },
      };

    case ActionCreators.EnableGroundStations.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                groundStationsEnabled: true,
              },
            },
          },
        },
      };

    case ActionCreators.DisableGroundStations.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                groundStationsEnabled: false,
              },
            },
          },
        },
      };

    case ActionCreators.AddBeamToMap.type: {
      const { beamId } = action.payload;

      if (!beamId) {
        return currentState;
      }

      const currBeamsOnMap = new Set<BeamOnMap>(
        mapFields?.overlaysUiState[currentState.overlaysCurrentContext]?.beamsOnMap,
      );
      currBeamsOnMap.add(action.payload);
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                beamsOnMap: Array.from(currBeamsOnMap),
              },
            },
          },
        },
      };
    }

    case ActionCreators.RemoveBeamFromMap.type: {
      const { beamId, networkType, mapType } = action.payload;

      if (!beamId || !networkType || !mapType) {
        return currentState;
      }

      const currBeamsOnMap = new Set<BeamOnMap>(
        mapFields?.overlaysUiState[currentState.overlaysCurrentContext]?.beamsOnMap.filter((beamOnMap: BeamOnMap) => {
          let keepBeam = true;
          if (
            beamOnMap.beamId === action.payload.beamId &&
            beamOnMap.networkType === action.payload.networkType &&
            beamOnMap.mapType === action.payload.mapType
          ) {
            keepBeam = false;
          }
          return keepBeam;
        }),
      );

      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                beamsOnMap: Array.from(currBeamsOnMap),
              },
            },
          },
        },
      };
    }

    case ActionCreators.AddHubsToMap.type: {
      const hubIds: string[] = action.payload.hubs;
      const currentHubsOnMap = new Set<string>(
        mapFields?.overlaysUiState[currentState.overlaysCurrentContext]?.hubsOnMap,
      );

      if (!isNil(hubIds)) {
        hubIds.forEach((hubId: string) => {
          currentHubsOnMap.add(hubId);
        });
      }
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                hubsOnMap: Array.from(currentHubsOnMap),
              },
            },
          },
        },
      };
    }

    case ActionCreators.SetHubsOnMap.type: {
      const hubIds: string[] = action.payload.hubs;

      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                hubsOnMap: Array.from(hubIds),
              },
            },
          },
        },
      };
    }

    case ActionCreators.ClearBeamsOnMap.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                beamsOnMap: [],
                hubsOnMap: [], // clear the hubs as well
                groundStationsOnMap: [],
              },
            },
          },
        },
      };
    }

    case ActionCreators.AddGroundStationsToMap.type: {
      const groundStations: GroundStation[] = action.payload.groundStations;
      const currentGroundStationsOnMap = new Set<GroundStation>(
        mapFields?.overlaysUiState[currentState.overlaysCurrentContext]?.groundStationsOnMap,
      );

      if (!isNil(groundStations)) {
        groundStations.forEach((groundStation: GroundStation) => {
          currentGroundStationsOnMap.add(groundStation);
        });
      }
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                groundStationsOnMap: Array.from(currentGroundStationsOnMap),
              },
            },
          },
        },
      };
    }

    case ActionCreators.SetGroundStationsOnMap.type: {
      const groundStations: GroundStation[] = action.payload.groundStations;
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                groundStationsOnMap: Array.from(groundStations),
              },
            },
          },
        },
      };
    }

    case ActionCreators.ClearGroundStationsOnMap.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                groundStationsOnMap: [],
              },
            },
          },
        },
      };
    }

    case ActionCreators.SetWeatherOverlayShown.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                weatherShown: action.payload,
              },
            },
          },
        },
      };
    }

    // TAIL TAG OPTIONS

    case ActionCreators.ClearCurrentOverlaysContextExceptTailTagOptions.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...defaultOverlaysState,
                antennaDropDownDisabled:
                  currentState.overlaysCurrentContext !== OverlaysContextsEnum.FLEET_VIEW &&
                  currentState.overlaysCurrentContext !== OverlaysContextsEnum.TRANSPORT_MONITORING,
                currentNextBeamsDisabled: currentState.overlaysCurrentContext !== OverlaysContextsEnum.FTV,
                tailTagsShown: mapFields.overlaysUiState[currentState.overlaysCurrentContext]?.tailTagsShown,
                terminalTagSize: mapFields.overlaysUiState[currentState.overlaysCurrentContext]?.terminalTagSize,
              },
            },
          },
        },
      };
    }

    case ActionCreators.SetTailTagsShown.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields.overlaysUiState[currentState.overlaysCurrentContext],
                tailTagsShown: action.payload.shown,
              },
            },
          },
        },
      };
    }

    case ActionCreators.SetTerminalTagsSize.type:
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields.overlaysUiState[currentState.overlaysCurrentContext],
                terminalTagSize: action.payload.tailTagSize,
              },
            },
          },
        },
      };

    case ActionCreators.UpdateCurrentOverlaysContext.type: {
      const beams = action.payload.geojson.response;

      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                selectedSscfVersion:
                  beams.properties?.versions.find((v: string) => v.startsWith(AL_MAP_VERSION_PREFIX))?.split('-')[1] ??
                  undefined,
                kaHnaZonesDisabled: !beams.features.filter(
                  (feature: Feature) =>
                    feature.properties?.mapType === 'HNA' && feature.properties?.network === 'VIASAT',
                ).length,
                kuHnaZonesDisabled: !beams.features.filter(
                  (feature: Feature) =>
                    feature.properties?.mapType === 'HNA' && feature.properties?.network === 'VGNET',
                ).length,
                hcxHnaZonesDisabled: !beams.features.filter(
                  (feature: Feature) =>
                    feature.properties?.mapType === 'HNA' && feature.properties?.network === 'I5_HCX',
                ).length,
                gxHnaZonesDisabled: !beams.features.filter(
                  (feature: Feature) => feature.properties?.mapType === 'HNA' && feature.properties?.network === 'GX',
                ).length,
              },
            },
          },
        },
      };
    }

    case ActionCreators.SetOpenedExpansionPanelId.type: {
      const panelId = action.payload.openedExpansionPanelId;

      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                openedExpansionPanelId:
                  mapFields.overlaysUiState[currentState.overlaysCurrentContext]?.openedExpansionPanelId === panelId
                    ? null
                    : panelId,
              },
            },
          },
        },
      };
    }

    case ActionCreators.SetCurrentBeams.type: {
      const beamsOnMap = action.payload.beamsOnMap;

      if (
        !beamsOnMap ||
        beamsOnMap.length === 0 ||
        isEqual(beamsOnMap, mapFields.overlaysUiState[currentState.overlaysCurrentContext]?.currentBeams)
      ) {
        return currentState;
      }
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                currentBeams: beamsOnMap,
              },
            },
          },
        },
      };
    }

    case ActionCreators.SetNextBeam.type: {
      const nextBeam = action.payload.nextBeam;
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                nextBeam: nextBeam,
              },
            },
          },
        },
      };
    }

    case ActionCreators.ToggleOverlaidFlightLegVisibility.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaidFlightLegHidden: !mapFields.overlaidFlightLegHidden,
            overlaysUiState: {
              ...mapFields.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields.overlaysUiState[currentState.overlaysCurrentContext],
                flightPathBeamsDisabled: !mapFields.overlaidFlightLegHidden,
                flightPathWaypointsDisabled: !mapFields.overlaidFlightLegHidden,
              },
            },
          },
        },
      };
    }

    case ActionCreators.ShowOverlaidFlightLeg.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaidFlightLegHidden: false,
            overlaysUiState: {
              ...mapFields.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields.overlaysUiState[currentState.overlaysCurrentContext],
                flightPathBeamsDisabled: false,
                flightPathBeamsChecked: true,
                flightPathWaypointsDisabled: false,
                flightPathWaypointsChecked: true,
              },
            },
          },
        },
      };
    }

    case ActionCreators.SetFlightPathBeamsDisabled.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields.overlaysUiState[currentState.overlaysCurrentContext],
                flightPathBeamsDisabled: action.payload.disabled,
              },
            },
          },
        },
      };
    }

    case ActionCreators.SetFlightPathBeamsChecked.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields.overlaysUiState[currentState.overlaysCurrentContext],
                flightPathBeamsChecked: action.payload.checked,
              },
            },
          },
        },
      };
    }
    case ActionCreators.SetFlightPathWaypointsDisabled.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields.overlaysUiState[currentState.overlaysCurrentContext],
                flightPathWaypointsDisabled: action.payload.disabled,
              },
            },
          },
        },
      };
    }

    case ActionCreators.SetFlightPathWaypointsChecked.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields.overlaysUiState[currentState.overlaysCurrentContext],
                flightPathWaypointsChecked: action.payload.checked,
              },
            },
          },
        },
      };
    }
    case ActionCreators.ClearMission.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaidFlightLegHidden: true,
            overlaysUiState: {
              ...mapFields.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields.overlaysUiState[currentState.overlaysCurrentContext],
                flightPathBeamsDisabled: true,
                flightPathBeamsChecked: false,
                flightPathWaypointsDisabled: true,
                flightPathWaypointsChecked: false,
              },
            },
          },
        },
      };
    }

    case ActionCreators.SetCurrentNextBeamsChecked.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields.overlaysUiState[currentState.overlaysCurrentContext],
                currentNextBeamsChecked: action.payload.checked,
              },
            },
          },
        },
      };
    }

    case ActionCreators.SetCurrentNextBeamsDisabled.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields.overlaysUiState[currentState.overlaysCurrentContext],
                currentNextBeamsDisabled: action.payload.disabled,
              },
            },
          },
        },
      };
    }

    case ActionCreators.SetZoomLatLng.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            zoomLatLng: action.payload.latLng,
          },
        },
      };
    }

    case ActionCreators.SetSavedZoomSettings.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            savedZoomSettings: action.payload.center && action.payload.zoom !== undefined ? action.payload : undefined,
          },
        },
      };
    }

    case ActionCreators.SetMapHidden.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            hidden: action.payload.hidden,
          },
        },
      };
    }

    case ActionCreators.SetGpsSaMapLayersChecked.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                gpsSaMapLayersChecked: action.payload.checked,
              },
            },
          },
        },
      };
    }

    case ActionCreators.SetGpsSaMapLayersLowAccuracyChecked.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                gpsSaMapLayersLowAccuracyChecked: action.payload.checked,
              },
            },
          },
        },
      };
    }

    case ActionCreators.SetGpsSaMapLayersReducedAccuracyChecked.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                gpsSaMapLayersReducedAccuracyChecked: action.payload.checked,
              },
            },
          },
        },
      };
    }

    case ActionCreators.SetGpsSaMapLayersNormalAccuracyChecked.type: {
      return {
        ...currentState,
        mapContext: {
          ...currentState.mapContext,
          [mapId]: {
            ...mapFields,
            overlaysUiState: {
              ...mapFields?.overlaysUiState,
              [currentState.overlaysCurrentContext]: {
                ...mapFields?.overlaysUiState[currentState.overlaysCurrentContext],
                gpsSaMapLayersNormalAccuracyChecked: action.payload.checked,
              },
            },
          },
        },
      };
    }

    default:
      return currentState;
  }

  //  return { ...state, ...partialState };
}
